// "use client";
import { Box, Card, Stack, Typography } from "@mui/material";
import { FC, memo, useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Button } from "../../Button";
import { TextInput } from "../../Fields/TextField";
import { Select } from "../../Fields/SelectField";
import { DateInput } from "../../Fields/DataPickerField";
import { CheckBox } from "../../Fields/CheckBox";
import { WebShopIcon } from "../../WebShopIcon";
import useToggle from "@/src/hooks/useToggle";
import { cn } from "@/src/shared/utils/mergeTwClasses";
import { DeleteTouristModal } from "./DeleteTouristModal";
import {
  parseIdentCode,
  transliterateFromCyrillic,
} from "@/src/shared/utils/string";
import { isNil } from "lodash";
import { MESSAGES } from "@/src/shared/utils/validationRules";
import { getAgeFromDate } from "@/src/shared/utils/dateTime";

interface InsuredTouristProps {
  heading: string;
  handleDelete: () => void;
  onAddressCheckboxChange: (index: number, isSameAddress: boolean) => void;
  index: number;
  showSameAddressCheckbox: boolean;
  showDeleteButton: boolean;
  readOnly: boolean;
  isContentExpanded: boolean;
  style?: React.CSSProperties;
  touristsListItemFieldName: string;
  tourist_lastNameProps: any;
  tourist_firstNameProps: any;
  tourist_lastNameLatin: any;
  tourist_firstNameLatin: any;
}

export const InsuredTourist: FC<any> = memo(
  ({
    heading,
    handleDelete,
    onAddressCheckboxChange,
    index,
    showSameAddressCheckbox,
    showDeleteButton,
    readOnly,
    isContentExpanded,
    onToggleExpansion,
    style,
    touristsListItemFieldName,
    tourist_lastNameProps,
    tourist_firstNameProps,
    tourist_lastNameLatinProps,
    tourist_firstNameLatinProps,
    tourist_citizenshipProps,
    tourist_foreignPassportNumberProps,
    tourist_identCodeProps,
    tourist_birthDateProps,
    tourist_cityProps,
    tourist_streetProps,
    tourist_streetTypeProps,
    tourist_houseNumberProps,
    tourist_flatNumberProps,
    ...rest
  }) => {
    const {
      isToggled: isOpenedDeleteTouristModal,
      open: openDeleteTouristModal,
      close: closeDeleteTouristModal,
    } = useToggle({ initialState: false });

    const { control, setValue, setError, clearErrors, watch, formState } =
      useFormContext();

      const identCodeVal = watch(
      `${touristsListItemFieldName}.tourist_identCode`,
    );
    const birthDateVal = watch(
      `${touristsListItemFieldName}.tourist_birthDate`,
    );

      const errors = formState.errors;


      useEffect(() => {
          const listErrors = errors?.forTourists;

          if (!Array.isArray(listErrors)) return;

          const cardErrors = listErrors[index];

          const hasErrors =
              cardErrors &&
              typeof cardErrors === "object" &&
              Object.keys(cardErrors).length > 0;

          if (hasErrors && !isContentExpanded) {
              onToggleExpansion(index);
          }
      }, [errors, isContentExpanded, index, onToggleExpansion]);


    useEffect(() => {
      if (identCodeVal && !readOnly) {
        const parsedIdentDate = parseIdentCode(identCodeVal);
        if (parsedIdentDate) {
          setValue(
            `${touristsListItemFieldName}.tourist_birthDate`,
            parsedIdentDate,
            { shouldValidate: true },
          );
        }
      }
    }, [identCodeVal, readOnly, setValue, touristsListItemFieldName]);

    useEffect(() => {
      if (identCodeVal && birthDateVal && !readOnly) {
        const parsedIdentDate = parseIdentCode(identCodeVal);

        if (!parsedIdentDate) {
          setError(`${touristsListItemFieldName}.tourist_identCode`, {
            type: "validate",
            message: MESSAGES.INVALID_IDENT_CODE,
          });
          return;
        }

        // birthDateVal до "yyyy-MM-dd"
        const userSelectedDate = birthDateVal.slice(0, 10);
        if (parsedIdentDate === userSelectedDate) {
            clearErrors(`${touristsListItemFieldName}.tourist_birthDate`);
        } else {
            setError(`${touristsListItemFieldName}.tourist_birthDate`, {
                type: "validate",
                message: MESSAGES.INVALID_IDENT_CODE_WITH_BIRTH_DATE,
            });
        }
      } else {
        clearErrors(`${touristsListItemFieldName}.tourist_identCode`);
      }
    }, [
      identCodeVal,
      birthDateVal,
      readOnly,
      setError,
      clearErrors,
      touristsListItemFieldName,
    ]);

    const handleCyrillicLastNameChange = (
      e?: React.ChangeEvent<HTMLInputElement>,
    ) => {
      const newValue = e?.target?.value;
      if (!isNil(newValue)) {
        const translit = transliterateFromCyrillic(newValue, true);
        setValue(
          `${touristsListItemFieldName}.tourist_lastNameLatin`,
          translit,
          {
            shouldValidate: true,
          },
        );
      }
    };

    const handleCyrillicFirstNameChange = (
      e?: React.ChangeEvent<HTMLInputElement>,
    ) => {
      const newValue = e?.target?.value;
      if (!isNil(newValue)) {
        const translit = transliterateFromCyrillic(newValue, true);
        setValue(
          `${touristsListItemFieldName}.tourist_firstNameLatin`,
          translit,
          { shouldValidate: true },
        );
      }
    };

    const isSameAddress = useWatch({
      control,
      name: `${touristsListItemFieldName}.isSameAddress`,
    });

    const userAge = useMemo(
      () => getAgeFromDate(birthDateVal) ?? Infinity,
      [birthDateVal],
    );

    return (
      <Card
        className={cn("flex w-full flex-col bg-super-light-blue p-4 md:p-6")}
        style={style}
        {...rest}
      >
        <Stack className="mb-3 w-full justify-between text-lg flex-center-row">
          <Typography component={"h4"} variant="h2" className="text-lg font-semibold">
            {heading}
          </Typography>
          {showDeleteButton && (
            <Button
              startIcon={<WebShopIcon name="minus" />}
              variant="text"
              fullWidth={false}
              onClick={openDeleteTouristModal}
            >
              видалити
            </Button>
          )}
        </Stack>

        <Stack className="flex w-full flex-col gap-4">
          <Box className="grid grid-cols-2 gap-4 max-md:grid-cols-1">
            <TextInput
              {...tourist_lastNameProps}
              readOnly={readOnly}
              name={`${touristsListItemFieldName}.tourist_lastName`}
              onChange={handleCyrillicLastNameChange}
              maskType={'capitalizeFirstWord'}
            />
            <TextInput
              {...tourist_firstNameProps}
              readOnly={readOnly}
              name={`${touristsListItemFieldName}.tourist_firstName`}
              onChange={handleCyrillicFirstNameChange}
              maskType={'capitalizeFirstWord'}
            />
          </Box>
          <Box
            className={cn(
              "flex flex-col gap-4 transition-[max-height,opacity] duration-200 ease-in-out",
              isContentExpanded
                ? "max-h-[1500px] opacity-100"
                : "max-h-0 opacity-0",
            )}
          >
            <Box className={cn("grid grid-cols-2 gap-4 max-md:grid-cols-1")}>
              <TextInput
                {...tourist_lastNameLatinProps}
                name={`${touristsListItemFieldName}.tourist_lastNameLatin`}
                readOnly={readOnly}
                maskType={'capitalizeFirstWord'}
              />
              <TextInput
                {...tourist_firstNameLatinProps}
                name={`${touristsListItemFieldName}.tourist_firstNameLatin`}
                readOnly={readOnly}
                maskType={'capitalizeFirstWord'}
              />
              <Select
                {...tourist_citizenshipProps}
                name={`${touristsListItemFieldName}.tourist_citizenship`}
                readOnly={true}
                disabled
              />
              <TextInput
                {...tourist_foreignPassportNumberProps}
                name={`${touristsListItemFieldName}.tourist_foreignPassportNumber`}
                readOnly={readOnly}
                maskType={"passportSerie"}
                pattern={"^[A-Za-z]{2}\\d{6}$"}
              />
              <TextInput
                {...tourist_identCodeProps}
                required={userAge >= 18}
                placeholder={"1234567899"}
                name={`${touristsListItemFieldName}.tourist_identCode`}
                readOnly={readOnly}
                checkIPN={!!identCodeVal}
              />
              <DateInput
                {...tourist_birthDateProps}
                name={`${touristsListItemFieldName}.tourist_birthDate`}
                readOnly={readOnly}
                ipnFieldName={`${touristsListItemFieldName}.tourist_identCode`}
              />
            </Box>

            {showSameAddressCheckbox && (
              <CheckBox
                readOnly={readOnly}
                control={control}
                name={`${touristsListItemFieldName}.isSameAddress`}
                onChange={(_, value) => {
                  onAddressCheckboxChange(index, !!value);
                }}
                label="Адреса реєстрації застрахованого співпадає з адресою страхувальника."
              />
            )}
            <Box
              className={cn(
                "grid w-full grid-cols-1 gap-4 transition-[max-height,opacity] duration-0 ease-in-out",
                isSameAddress
                  ? "max-h-0 opacity-0"
                  : "max-h-[1500px] opacity-100",
              )}
            >
              <Select
                {...tourist_cityProps}
                readOnly={readOnly}
                name={`${touristsListItemFieldName}.tourist_city`}
                disabled={readOnly}
                hintWrapperClassname="w-full"
              />
              <Box className="grid grid-cols-6 gap-4 max-md:grid-cols-1">
                <Select
                  {...tourist_streetTypeProps}
                  name={`${touristsListItemFieldName}.tourist_streetType`}
                  disabled={readOnly}
                  hintWrapperClassname="col-span-2 max-md:col-span-1"
                  readOnly={readOnly}
                />
                <TextInput
                  {...tourist_streetProps}
                  readOnly={readOnly}
                  name={`${touristsListItemFieldName}.tourist_street`}
                  disableContextFieldUpdate
                  hintWrapperClassname="col-span-2 max-md:col-span-1"
                />
                <TextInput
                  {...tourist_houseNumberProps}
                  readOnly={readOnly}
                  name={`${touristsListItemFieldName}.tourist_houseNumber`}
                  hintWrapperClassname="col-span-1 max-md:col-span-1"
                />
                <TextInput
                  {...tourist_flatNumberProps}
                  readOnly={readOnly}
                  name={`${touristsListItemFieldName}.tourist_flatNumber`}
                  hintWrapperClassname="col-span-1 max-md:col-span-1"
                />
              </Box>
            </Box>
          </Box>
        </Stack>

        <Stack className="w-full justify-between flex-center-row">
          <Button
            endIcon={
              <WebShopIcon
                name="arrowDown"
                className="transition-transform"
                style={{
                  transform: `rotate(${isContentExpanded ? "180deg" : "0deg"})`,
                }}
              />
            }
            variant="text"
            fullWidth={false}
            onClick={() => onToggleExpansion(index)}
          >
            {isContentExpanded ? "Показати менше" : "Показати більше"}
          </Button>
        </Stack>

        <DeleteTouristModal
          isOpen={isOpenedDeleteTouristModal}
          onClose={closeDeleteTouristModal}
          onCancel={closeDeleteTouristModal}
          onAccept={handleDelete}
        />
      </Card>
    );
  },
);

InsuredTourist.displayName = "InsuredTourist";
